import Container, { Service } from 'typedi';
import { WindowToken } from '../../injection-tokens';
import { StateTagGroup, StateType } from '../../models/enums/general-enums';
import { LoggerProvider } from '../logger';
import { ILogger, ILoginSuccessData } from '@sparkware/uc-sdk-core';
import { ClickStreamTrackingProvider } from '../tracking';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
    Level,
} from '@unified-client/event-formatter';
import { WebPushElasticEvent, WebPushRejectionReason } from './enums/consts';
import { v4 as uuIdV4 } from 'uuid';
import IWebPushClickStreamEventData from './models/IWebPushClickStreamEventData';
import { CookieStoreItemService } from '../storage/implementations';
import DeferredObject from '../../../Modules/Utils/DeferredObject';
import { IUASDKPromiseResult } from './models/IUASDKPromiseResult';
import { ServiceWorkerService } from './service.worker.service';
import { IWebPushService } from './models/IWebPushService';
import { IUrbanAirshipSDK } from './models/IUrbanAirshipSDK';

@Service()
export class WebPushService implements IWebPushService {
    private _uaSdkDO: DeferredObject<IUASDKPromiseResult>;
    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _cookieStoreService: CookieStoreItemService;
    private readonly _serviceWorkerService: ServiceWorkerService;
    private readonly _correlationID: string;
    private readonly _urbanAirshipPropertyName: string = 'UA';

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('WebPushService');
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._window = Container.get(WindowToken);
        this._cookieStoreService = Container.get(CookieStoreItemService);
        this._serviceWorkerService = Container.get(ServiceWorkerService);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('WebPushService');
        this._correlationID = uuIdV4();
        this._uaSdkDO = new DeferredObject<IUASDKPromiseResult>({ storeResolved: true });
        this._logger.debug('Initialise urban airship web push service');
    }

    public onDomReady = async () => {
        this._appendUrbanAirshipScript();
        await this._initializeUrbanAirship();
    };

    public onLoginSuccess = async (data: ILoginSuccessData) => {
        const result = await this._uaSdkDO.promise;
        if (result?.sdk && !result?.error) {
            const userCid = data.AuthenticateData?.CID;
            const { sdk } = result;
            if (userCid) {
                sdk.channel.namedUser.set(userCid.toString());
            }
        }
    };

    private _appendUrbanAirshipScript = () => {
        const { appKey, token, vapidPublicKey } =
            this._window.pageContextManager.getAirshipData() || {};
        const script = document.createElement('script');
        script.setAttribute('data-ot-ignore', '');
        script.innerHTML = `!function (n, t, c, e, u) {
            function r(n) { try { f = n(u) } catch (n) { return h = n, void i(p, n) } i(s, f) } function i(n, t) {
                for (var c = 0; c < n.length; c++) d(n[c], t);
            } function o(n, t) { return n && (f ? d(n, f) : s.push(n)), t && (h ? d(t, h) : p.push(t)), l } function a(n) { return o(!1, n) } function d(t, c) {
                n.setTimeout(function () { t(c) }, 0)
            } var f, h, s = [], p = [], l = { then: o, catch: a, _setup: r }; n[e] = l; var v = t.createElement("script");
            v.src = c, v.async = !0, v.id = "_uasdk", v.rel = e, t.head.appendChild(v)
        }(window, document, 'https://web-sdk.urbanairship.com/notify/v1/ua-sdk.min.js',
            '${this._urbanAirshipPropertyName}', {
            appKey: "${appKey}",
            token: "${token}",
            vapidPublicKey: "${vapidPublicKey}",
            workerUrl: "${this._serviceWorkerService.serviceWorkerUrl}",
        });`;
        this._window.document.head.appendChild(script).parentNode?.removeChild(script);
    };

    private _setChannelCStatusTag = (sdk: IUrbanAirshipSDK) => {
        const cookie = this._cookieStoreService.get('cstatus');
        if (cookie) {
            const cStatus_tag_group = 'cstatus';
            sdk?.channel.tags.set(cookie, cStatus_tag_group);
        }
    };

    private _setChannelStateTag = (sdk: IUrbanAirshipSDK) => {
        const { isMultiStateSite, isVanillaSite } = this._window.pageContextManager.getSiteData();
        const { state } = this._window.pageContextManager.getGlobalizationData();
        if (isMultiStateSite) {
            const stateTag = isVanillaSite ? StateType.Vanilla : state;
            sdk?.channel.tags.set(stateTag, StateTagGroup.State);
        }
    };

    private _initializeUrbanAirship = async () => {
        const formatter = this._eventFormatterBuilder.createFormatter(
            'registerToUrbanAirshipEvent',
        );
        const eventData: IWebPushClickStreamEventData = {
            event: WebPushElasticEvent.RegisterUrbanAirship,
            isOk: true,
        };
        try {
            const hasNotificationPermission = this.shouldHaveNotificationPermission();
            if (hasNotificationPermission) {
                const urbanAirshipSDK = await this._window.UA;
                const { canRegister } = urbanAirshipSDK;
                if (canRegister) {
                    await this._registerUrbanAirship(formatter, eventData);
                } else {
                    eventData.isOk = false;
                    eventData.errorDescription = WebPushRejectionReason.CannotRegister.reason;
                    this._sendElasticEvent(formatter, eventData);
                    this._uaSdkDO.resolve({ error: WebPushRejectionReason.CannotRegister });
                }
            }
        } catch (err) {
            eventData.isOk = false;
            eventData.errorDescription = `Urban Airship SDK initialization failure: ${
                err?.stack || err || WebPushRejectionReason.UAPromiseRejection.reason
            }`;
            this._logger.error(`[_initializeUrbanAirship] ${eventData.errorDescription}`);
            this._sendElasticEvent(formatter, eventData, Level.error);
            this._uaSdkDO.resolve({ error: WebPushRejectionReason.UAPromiseRejection });
        }
    };

    private shouldHaveNotificationPermission = () => {
        if (!('Notification' in this._window)) {
            this._logger.debug(
                `[shouldHaveNotificationPermission] Notification API is not supported.`,
            );
            this._uaSdkDO.resolve({
                error: WebPushRejectionReason.NotificationApiNotSupported,
            });
            return false;
        } else if (Notification.permission !== 'granted') {
            this._logger.debug(
                `[shouldHaveNotificationPermission] ${WebPushRejectionReason.NotificationNotPermitted.reason}`,
            );
            this._uaSdkDO.resolve({ error: WebPushRejectionReason.NotificationNotPermitted });
            return false;
        }
        this._logger.debug(`[shouldHaveNotificationPermission] Notification permitted`);
        return true;
    };

    private _handleRegistrationError = (
        err: Error,
        formatter: any,
        eventData: IWebPushClickStreamEventData,
    ) => {
        eventData.isOk = false;
        const message =
            err?.message || WebPushRejectionReason.UncaughtExceptionDuringRegistration.reason;
        eventData.errorDescription = `Urban Airship SDK registration failure: ${message}`;
        this._logger.error(
            `[_registerUrbanAirship] error while registering sdk...`,
            err,
            eventData.errorDescription,
        );
        this._sendElasticEvent(formatter, eventData, Level.error);
        this._uaSdkDO.resolve({
            error: WebPushRejectionReason.UncaughtExceptionDuringRegistration,
        });
    };

    private _registerUrbanAirship = async (
        formatter: any,
        eventData: IWebPushClickStreamEventData,
    ) => {
        const urbanAirshipSDK = this._window.UA;
        const timeoutDuration = 10000; // 10 seconds
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Registration timeout')), timeoutDuration),
        );
        // DO NOT USE async await syntax due "this" binding problems inside the urban airship sdk
        urbanAirshipSDK
            .then(async (sdk: IUrbanAirshipSDK): Promise<void> => {
                try {
                    await Promise.race([sdk.register(), timeoutPromise]);
                    this._sendElasticEvent(formatter, eventData);
                    this._uaSdkDO.resolve({ sdk: sdk });
                    this._setChannelCStatusTag(sdk);
                    this._setChannelStateTag(sdk);
                    this._logger.debug(`[_registerUrbanAirship] successfully registered`);
                } catch (err) {
                    this._handleRegistrationError(err, formatter, eventData);
                }
            })
            .catch((err: Error) => {
                this._handleRegistrationError(err, formatter, eventData);
            });
    };

    private _sendElasticEvent = (
        formatter: any,
        eventData: IWebPushClickStreamEventData,
        level: Level = Level.information,
    ) => {
        const event = formatter.formatUCEvent(
            { message: eventData.event },
            { correlationID: this._correlationID, ...(level && { level }) },
            { ...eventData },
        );
        this._clickStreamTrackingProvider.sendEventV2(event);
    };
}
